type LogLevel = "debug" | "info" | "warn" | "error";

export type LogRecord = {
  ts: string;
  level: LogLevel;
  event: string;
  release?: string;
  platform?: string;
  data?: unknown;
};

type LogSink = (record: LogRecord) => void;

const REDACT_KEYS = [
  "token",
  "access_token",
  "authorization",
  "pin",
  "pin_code",
  "password",
  "secret",
] as const;

const RELEASE =
  import.meta.env.VITE_RELEASE ??
  import.meta.env.VITE_GIT_SHA ??
  import.meta.env.MODE;

const sinks = new Set<LogSink>();

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function shouldRedact(key: string): boolean {
  const lower = key.toLowerCase();
  return REDACT_KEYS.some((k) => lower.includes(k));
}

function sanitize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map((v) => sanitize(v));
  if (isPlainObject(value)) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = shouldRedact(k) ? "[REDACTED]" : sanitize(v);
    }
    return out;
  }
  if (typeof value === "string") {
    if (value.length > 300) return `${value.slice(0, 300)}…`;
    return value;
  }
  return value;
}

function emit(level: LogLevel, event: string, data?: unknown): void {
  const record: LogRecord = {
    ts: new Date().toISOString(),
    level,
    event,
    release: RELEASE,
    platform:
      typeof window !== "undefined" ? (window as { Capacitor?: { getPlatform?: () => string } }).Capacitor?.getPlatform?.() : undefined,
    data: data != null ? sanitize(data) : undefined,
  };

  const line = `[app:${record.level}] ${record.event}`;
  if (level === "error") {
    console.error(line, record);
  } else if (level === "warn") {
    console.warn(line, record);
  } else if (level === "debug") {
    console.debug(line, record);
  } else {
    console.log(line, record);
  }

  sinks.forEach((sink) => {
    try {
      sink(record);
    } catch (sinkError) {
      console.warn("[app:warn] logger.sink.failed", {
        ts: new Date().toISOString(),
        event: "logger.sink.failed",
        sinkError: sanitize(sinkError),
      });
    }
  });
}

export function registerLogSink(sink: LogSink): () => void {
  sinks.add(sink);
  return () => {
    sinks.delete(sink);
  };
}

export function clearLogSinksForTests(): void {
  if (import.meta.env.MODE !== "test") return;
  sinks.clear();
}

export const logger = {
  debug(event: string, data?: unknown): void {
    emit("debug", event, data);
  },
  info(event: string, data?: unknown): void {
    emit("info", event, data);
  },
  warn(event: string, data?: unknown): void {
    emit("warn", event, data);
  },
  error(event: string, data?: unknown): void {
    emit("error", event, data);
  },
};

